// =============================================================================
// app/src/lib/lerBoleto.ts — o código de barras do boleto, lido da IMAGEM.
//
// Pedido do dono (18/09/2026): "só com a imagem do boleto/pdf eu consigo
// cadastrar o boleto?". Os anexos que ele já sobe são digitalizações do
// iPhone: PDF sem texto nenhum, só a foto da folha dentro. Então o caminho é
// ler as BARRAS — o que o leitor do caixa do banco faz.
//
// O CÓDIGO DO BOLETO é ITF (Intercalado 2 de 5), 44 dígitos, e é simples de
// ler à mão: cada par de dígitos são 10 faixas — 5 barras (o 1º dígito) com 5
// espaços no meio (o 2º) —, e em cada grupo de 5 exatamente 2 são largas. Por
// isso aqui não entra biblioteca, como na etiqueta (barcode.ts): percorre
// linhas da imagem, mede a largura das faixas, e confere.
//
// A CONFERÊNCIA é o que torna isto seguro para dinheiro. Um código só vale se:
//   1. cada grupo de 5 tiver 2 largas nítidas (a razão larga/estreita passa
//      de 1,5), com início e fim do ITF no lugar;
//   2. os dígitos verificadores do boleto fecharem (conferirCodigo);
//   3. DUAS linhas diferentes da imagem lerem o MESMO código. O dígito geral
//      do boleto é módulo 11: um erro de leitura passa por ele de vez em
//      quando; o mesmo erro em duas linhas, raramente. E como o defeito
//      FÍSICO (vinco, tinta) faz todas as linhas errarem igual, há mais
//      travas: cada faixa larga do tamanho das largas vizinhas (a fundida
//      passa de 1,5×), todo par com a mesma largura, e dois códigos lidos no
//      MESMO lugar da imagem = não li (a não ser que um tenha 3× os votos do
//      outro). Revisão de 18/09/2026, duas rodadas.
// Sem as três, a resposta é "não li" — e a tela pede para colar o código.
//
// Puro e sem DOM: recebe a luminância (0–255, uma posição por pixel). O que
// fala com o navegador (arquivo → pixels) mora em lerBoletoArquivo.ts.
// =============================================================================
import { conferirCodigo } from './codigoBarras';

/** Os 10 dígitos do ITF, como larguras das 5 faixas: 1 = larga. */
const PADROES: Record<string, string> = {
  '00110': '0', '10001': '1', '01001': '2', '11000': '3', '00101': '4',
  '10100': '5', '01100': '6', '00011': '7', '10010': '8', '01010': '9',
};

/** 4 faixas de início + 22 pares × 10 + 3 de fim. */
const FAIXAS = 4 + 220 + 3;

export interface Luminancia {
  largura: number;
  altura: number;
  /** Um valor 0–255 por pixel, linha a linha. */
  pixels: Uint8Array | Uint8ClampedArray;
}

/** RGBA (o ImageData do canvas) → luminância. */
export function luminanciaDe(rgba: Uint8ClampedArray | Uint8Array, largura: number, altura: number): Luminancia {
  const pixels = new Uint8Array(largura * altura);
  for (let i = 0, j = 0; i < pixels.length; i += 1, j += 4) {
    pixels[i] = ((rgba[j] ?? 0) * 77 + (rgba[j + 1] ?? 0) * 150 + (rgba[j + 2] ?? 0) * 29) >> 8;
  }
  return { largura, altura, pixels };
}

/**
 * Larguras das faixas escuras e claras de uma linha, com a borda achada por
 * INTERPOLAÇÃO entre pixels: a barra estreita tem 2–3 px numa foto comum, e
 * contar pixel inteiro erraria a razão larga/estreita.
 *
 * O limiar é LOCAL (meio caminho entre o mais claro e o mais escuro de uma
 * janela): a foto do celular tem sombra de um lado e reflexo do outro, e um
 * limiar só para a linha inteira apagaria metade das barras.
 */
export function faixasDaLinha(linha: Float32Array): Array<{ escura: boolean; largura: number }> {
  const n = linha.length;
  if (n < FAIXAS * 2) return [];
  const janela = Math.max(16, Math.floor(n / 24));
  const limiar = new Float32Array(n);
  // Mínimo e máximo por blocos e interpolação entre centros: O(n), e a
  // sombra que atravessa a folha muda devagar perto do tamanho da janela.
  const blocos = Math.ceil(n / janela);
  const meio = new Float32Array(blocos);
  const contraste = new Float32Array(blocos);
  for (let b = 0; b < blocos; b += 1) {
    let min = 255;
    let max = 0;
    const ini = Math.max(0, b * janela - janela);
    const fim = Math.min(n, (b + 1) * janela + janela);
    for (let x = ini; x < fim; x += 1) {
      const v = linha[x] ?? 255;
      if (v < min) min = v;
      if (v > max) max = v;
    }
    meio[b] = (min + max) / 2;
    contraste[b] = max - min;
  }
  for (let x = 0; x < n; x += 1) {
    const pos = x / janela - 0.5;
    const b0 = Math.max(0, Math.min(blocos - 1, Math.floor(pos)));
    const b1 = Math.min(blocos - 1, b0 + 1);
    const t = Math.max(0, Math.min(1, pos - b0));
    // Sem contraste (papel em branco) o limiar desce: nada vira barra.
    const c = (contraste[b0] ?? 0) * (1 - t) + (contraste[b1] ?? 0) * t;
    const m = (meio[b0] ?? 0) * (1 - t) + (meio[b1] ?? 0) * t;
    limiar[x] = c < 48 ? -1 : m;
  }

  const faixas: Array<{ escura: boolean; largura: number }> = [];
  let escura = (linha[0] ?? 255) < (limiar[0] ?? 0);
  let borda = 0;
  for (let x = 1; x < n; x += 1) {
    const agora = (linha[x] ?? 255) < (limiar[x] ?? 0);
    if (agora === escura) continue;
    const a = linha[x - 1] ?? 0;
    const b = linha[x] ?? 0;
    const lim = ((limiar[x - 1] ?? 0) + (limiar[x] ?? 0)) / 2;
    const frac = a === b ? 0.5 : Math.max(0, Math.min(1, (lim - a) / (b - a)));
    const cruzou = x - 1 + frac;
    faixas.push({ escura, largura: cruzou - borda });
    borda = cruzou;
    escura = agora;
  }
  faixas.push({ escura, largura: n - borda });
  return faixas;
}

/** Um grupo de 5 larguras → dígito e quais são largas, ou nulo se as 2
 *  largas não forem nítidas. */
function digito(larg: number[]): { d: string; larga: boolean[] } | null {
  const ordem = larg.map((v, i) => [v, i] as const).sort((p, q) => q[0] - p[0]);
  const menorLarga = Math.min(ordem[0]![0], ordem[1]![0]);
  const maiorEstreita = ordem[2]![0];
  if (menorLarga < maiorEstreita * 1.5) return null;
  const larga = [0, 1, 2, 3, 4].map((i) => i === ordem[0]![1] || i === ordem[1]![1]);
  const d = PADROES[larga.map((l) => (l ? '1' : '0')).join('')];
  return d === undefined ? null : { d, larga };
}

const mediana = (v: number[]) => {
  const o = [...v].sort((a, b) => a - b);
  return o.length ? o[Math.floor(o.length / 2)]! : 0;
};

/**
 * Procura um ITF de 44 dígitos numa sequência de faixas, lida num sentido.
 * Devolve os 44 dígitos crus (sem conferir o boleto) ou nulo.
 */
export function itfDasFaixas(faixas: Array<{ escura: boolean; largura: number }>): string[] {
  const achados: string[] = [];
  for (let i = 1; i + FAIXAS <= faixas.length; i += 1) {
    const f = faixas[i]!;
    if (!f.escura) continue;
    // Início: 4 estreitas (barra, espaço, barra, espaço), e antes delas uma
    // margem clara bem maior — sem isso, qualquer texto vira candidato.
    const inicio = [0, 1, 2, 3].map((k) => faixas[i + k]!.largura);
    const estreita = inicio.reduce((s, v) => s + v, 0) / 4;
    if (Math.max(...inicio) > estreita * 1.8 || Math.min(...inicio) < estreita * 0.4) continue;
    if (faixas[i - 1]!.largura < estreita * 5) continue;

    let digitos = '';
    let ok = true;
    const larguraDoPar: number[] = [];
    // Por par: as larguras das largas e das estreitas, para a conferência
    // de baixo.
    const largas: number[][] = [];
    const estreitas: number[][] = [];
    for (let par = 0; par < 22 && ok; par += 1) {
      const base = i + 4 + par * 10;
      const barras: number[] = [];
      const espacos: number[] = [];
      let soma = 0;
      for (let k = 0; k < 10; k += 1) {
        const larg = faixas[base + k]!.largura;
        if (larg > estreita * 5) { ok = false; break; }
        soma += larg;
        if (k % 2 === 0) barras.push(larg); else espacos.push(larg);
      }
      if (!ok) break;
      const a = digito(barras);
      const b = digito(espacos);
      if (a === null || b === null) { ok = false; break; }
      digitos += a.d + b.d;
      larguraDoPar.push(soma);
      const w: number[] = [];
      const n: number[] = [];
      [...barras, ...espacos].forEach((v, k) => ((k < 5 ? a.larga[k] : b.larga[k - 5]) ? w : n).push(v));
      largas.push(w);
      estreitas.push(n);
    }
    if (!ok) continue;
    // Cada par tem sempre 6 estreitas e 4 largas: a largura dele muda devagar
    // (perspectiva), nunca de um par para o outro.
    const tipica = mediana(larguraDoPar);
    if (larguraDoPar.some((w) => w < tipica * 0.75 || w > tipica * 1.33)) continue;
    // A LEITURA DESLOCADA (revisão de 18/09/2026, duas rodadas): um vinco na
    // folha racha uma barra larga (+2 faixas) e a tinta funde a barra do fim
    // com as vizinhas (−2); como o defeito é físico, TODAS as linhas erram
    // igual, a votação não ajuda, e o módulo 11 deixa passar 1 em 11. O que
    // denuncia é a largura: a fundida mede larga + 2 estreitas (1,7–1,8× uma
    // larga), e as metades da rachada ficam fora do tamanho das estreitas.
    // Cada faixa é comparada com as largas e estreitas dos pares VIZINHOS —
    // a foto tirada de lado muda o tamanho ao longo do código.
    const local = (lista: number[][], p: number) =>
      mediana(lista.slice(Math.max(0, p - 3), Math.min(22, p + 4)).flat());
    let coerente = true;
    for (let p = 0; p < 22 && coerente; p += 1) {
      const W = local(largas, p);
      const N = local(estreitas, p);
      if (largas[p]!.some((v) => v > W * 1.5 || v < W * 0.55)) coerente = false;
      if (estreitas[p]!.some((v) => v > N * 1.8)) coerente = false;
    }
    if (!coerente) continue;
    // Fim: barra LARGA do tamanho das outras largas, espaço e barra estreitos.
    const fim = i + 4 + 220;
    const larga = faixas[fim]!.largura;
    const Wfim = local(largas, 21);
    const Nfim = local(estreitas, 21);
    if (!faixas[fim]!.escura || larga > Wfim * 1.5 || larga < Wfim * 0.55
        || faixas[fim + 1]!.largura > Nfim * 1.8 || faixas[fim + 2]!.largura > Nfim * 1.8) continue;
    achados.push(digitos);
  }
  return achados;
}

export type Leitura = { linha: string; tipo: 'boleto' | 'convenio'; votos: number };

/**
 * Lê os códigos de barras de boleto de uma imagem, em qualquer das 4 posições
 * (a folha escaneada de lado é comum). Vazio quando não achou com segurança;
 * mais de um quando a folha tem duas parcelas — o mais bem lido primeiro.
 */
export function lerCodigosDeBarras(img: Luminancia, votosMinimos = 2): Leitura[] {
  const { largura: w, altura: h, pixels } = img;
  // Por código: quantas linhas o leram, e ONDE (a posição de cada linha) —
  // é a posição que separa "duas parcelas na folha" de "um código lido de
  // dois jeitos".
  const votos = new Map<string, { tipo: 'boleto' | 'convenio'; eixo: 'h' | 'v'; onde: number[] }>();

  const tentar = (linha: Float32Array, eixo: 'h' | 'v', pos: number) => {
    const faixas = faixasDaLinha(linha);
    if (faixas.length < FAIXAS) return;
    for (const sentido of [faixas, [...faixas].reverse()]) {
      for (const bruto of itfDasFaixas(sentido)) {
        const r = conferirCodigo(bruto);
        if (!r.ok) continue;
        const atual = votos.get(r.linha) ?? { tipo: r.tipo, eixo, onde: [] };
        if (atual.onde[atual.onde.length - 1] !== pos) atual.onde.push(pos);
        votos.set(r.linha, atual);
      }
    }
  };

  // Linhas horizontais e depois colunas; passo que dá ~300 linhas pela imagem
  // — a barra do boleto tem 13 mm de altura, e isso é 20+ linhas por ela.
  const passoH = Math.max(1, Math.floor(h / 300));
  const linhaH = new Float32Array(w);
  for (let y = 0; y < h; y += passoH) {
    for (let x = 0; x < w; x += 1) linhaH[x] = pixels[y * w + x] ?? 255;
    tentar(linhaH, 'h', y);
  }
  if (![...votos.values()].some((v) => v.onde.length >= votosMinimos)) {
    const passoV = Math.max(1, Math.floor(w / 300));
    const linhaV = new Float32Array(h);
    for (let x = 0; x < w; x += passoV) {
      for (let y = 0; y < h; y += 1) linhaV[y] = pixels[y * w + x] ?? 255;
      tentar(linhaV, 'v', x);
    }
  }

  const lidas = [...votos]
    .filter(([, v]) => v.onde.length >= votosMinimos)
    .map(([linha, v]) => ({ linha, tipo: v.tipo, votos: v.onde.length, eixo: v.eixo,
      de: Math.min(...v.onde), ate: Math.max(...v.onde) }))
    .sort((a, b) => b.votos - a.votos);
  // Dois códigos lidos na MESMA faixa da imagem são o mesmo código de barras
  // lido de dois jeitos (borrão, vinco): vale o que tem 3× mais votos; sem
  // essa folga, não se sabe qual é o certo e nenhum dos dois vale — a tela
  // pede para digitar. Faixas separadas são boletos diferentes (a folha com
  // duas parcelas), e cada um vale por si.
  const aceitas: typeof lidas = [];
  const duvidosas = new Set<string>();
  for (const l of lidas) {
    const rival = aceitas.find((a) => a.eixo === l.eixo && l.de <= a.ate && a.de <= l.ate);
    if (!rival) { aceitas.push(l); continue; }
    if (rival.votos < l.votos * 3) duvidosas.add(rival.linha);
  }
  return aceitas
    .filter((l) => !duvidosas.has(l.linha))
    .map(({ linha, tipo, votos: n }) => ({ linha, tipo, votos: n }));
}

/** O código mais bem lido da imagem, ou nulo. */
export function lerCodigoDeBarras(img: Luminancia, votosMinimos = 2): Leitura | null {
  return lerCodigosDeBarras(img, votosMinimos)[0] ?? null;
}

// --- As fotos de dentro do PDF ----------------------------------------------
//
// A digitalização do iPhone (e a de quase todo aplicativo de scanner) é um
// PDF com uma foto JPEG por página. Não precisa de leitor de PDF para achar
// essas fotos: cada uma é um "stream" com /DCTDecode no dicionário, e os bytes
// entre `stream` e `endstream` SÃO o JPEG, do jeito que um arquivo .jpg é.
// PDF de banco "de verdade" (o que chega por e-mail) desenha as barras como
// vetor, sem foto — as barras dele não se leem aqui, mas a linha digitável
// vem escrita no texto, e lerBoletoArquivo.ts a pega de lá (28/10/2026).

// Exportados para o lerDasArquivo (28/09/2026): o PDF de TEXTO anda no
// arquivo do mesmo jeito, so que com o filtro de dicionario INVERTIDO.
// Tres palavras export, e o filtro da foto fica byte por byte como esta.
export const latin1 = (b: Uint8Array, ini: number, fim: number) => {
  let s = '';
  for (let i = ini; i < fim; i += 1) s += String.fromCharCode(b[i] ?? 0);
  return s;
};

export function indiceDe(b: Uint8Array, alvo: string, desde: number): number {
  const c0 = alvo.charCodeAt(0);
  fora: for (let i = desde; i <= b.length - alvo.length; i += 1) {
    if (b[i] !== c0) continue;
    for (let k = 1; k < alvo.length; k += 1) if (b[i + k] !== alvo.charCodeAt(k)) continue fora;
    return i;
  }
  return -1;
}

/**
 * O dicionário que termina logo antes de `fim` (o `>>` antes de `stream`),
 * achado contando o aninhamento de trás para frente: /DecodeParms << … >>
 * dentro dele não pode enganar. Nulo se o que vem antes não é `>>`.
 */
export function dicionarioAntes(pdf: Uint8Array, fim: number): string | null {
  let j = fim - 1;
  while (j > 0 && (pdf[j] === 0x20 || pdf[j] === 0x0a || pdf[j] === 0x0d || pdf[j] === 0x09)) j -= 1;
  if (pdf[j] !== 0x3e || pdf[j - 1] !== 0x3e) return null;
  let nivel = 0;
  for (let k = j; k > 0 && k > fim - 8000; k -= 1) {
    if (pdf[k] === 0x3e && pdf[k - 1] === 0x3e) { nivel += 1; k -= 1; continue; }
    if (pdf[k] === 0x3c && pdf[k - 1] === 0x3c) {
      nivel -= 1;
      if (nivel === 0) return latin1(pdf, k - 1, j + 1);
      k -= 1;
    }
  }
  return null;
}

/** Os JPEG embutidos num PDF, do maior para o menor. */
/** As fotos na ORDEM em que estão no arquivo — que é a ordem das páginas na
 *  digitalização. O lote de documentos do dia (lib/lote.ts) depende disso;
 *  o leitor de boleto, não, e por isso fotosDoPdf() ordena por tamanho. */
export function fotosDoPdfEmOrdem(pdf: Uint8Array): Uint8Array[] {
  const fotos: Uint8Array[] = [];
  let desde = 0;
  for (;;) {
    const st = indiceDe(pdf, 'stream', desde);
    if (st < 0) break;
    // `endstream` também contém "stream": pula quem é o fim de outro stream.
    if (latin1(pdf, Math.max(0, st - 3), st) === 'end') { desde = st + 6; continue; }
    // Só vale a PALAVRA-CHAVE: logo depois de `>>` e seguida de fim de linha.
    // "Mainstream" num título, ou "stream" no meio de dados, não abre nada.
    let ini = st + 6;
    if (pdf[ini] === 0x0d) ini += 1;
    if (pdf[ini] !== 0x0a && pdf[ini - 1] !== 0x0d) { desde = st + 6; continue; }
    if (pdf[ini] === 0x0a) ini += 1;
    const dic = dicionarioAntes(pdf, st);
    if (dic === null) { desde = st + 6; continue; }
    const fimStream = indiceDe(pdf, 'endstream', ini);
    if (fimStream < 0) break;
    if (/\/DCTDecode/.test(dic) && !/\/FlateDecode/.test(dic)) {
      let fim = fimStream;
      while (fim > ini && (pdf[fim - 1] === 0x0a || pdf[fim - 1] === 0x0d)) fim -= 1;
      if (pdf[ini] === 0xff && pdf[ini + 1] === 0xd8) fotos.push(pdf.subarray(ini, fim));
    }
    desde = fimStream + 9;
  }
  return fotos;
}

/** As mesmas fotos, da maior para a menor: quem procura o código de barras
 *  quer a folha inteira antes da miniatura. */
export function fotosDoPdf(pdf: Uint8Array): Uint8Array[] {
  return fotosDoPdfEmOrdem(pdf).sort((a, b) => b.length - a.length);
}
